import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, CircularProgress, MenuItem, Paper, TextField, Typography } from '@mui/material'
import { groupCreationService } from '../../services/groupCreationService'

// Search existing groups by name and select one. `groupId` is the "all users"
// group whose admin permissions gate the search endpoint.
const GroupSearch = ({ groupId, selected, onSelect, onClear }) => {
  const [name, setName] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)

  useEffect(() => {
    if (selected || name.length < 2) {
      setResults([])
      return
    }
    const controller = new AbortController()
    setSearching(true)
    const timeout = setTimeout(() => {
      groupCreationService.searchGroups(groupId, name, controller.signal)
        .then(groups => setResults(groups || []))
        .catch(() => {})
        .finally(() => setSearching(false))
    }, 350)

    return () => {
      controller.abort()
      clearTimeout(timeout)
    }
  }, [groupId, name, selected])

  const clear = () => {
    setName('')
    setResults([])
    onClear()
  }

  if (selected) {
    return (
      <div className='flex items-center justify-between mb-5 p-3 border rounded'>
        <strong>{selected.name || 'Unnamed group'}</strong>
        <Button size='small' onClick={clear}>Change</Button>
      </div>
    )
  }

  return (
    <div className='mb-5'>
      <TextField
        variant='outlined'
        fullWidth
        label='Search group by name'
        value={name}
        onChange={e => setName(e.target.value)}
        InputProps={{ endAdornment: searching ? <CircularProgress size={18} /> : null }}
      />
      {results.length > 0 &&
        <Paper variant='outlined' className='mt-1 max-h-60 overflow-auto'>
          {results.map(group => (
            <MenuItem key={group.id} onClick={() => { onSelect(group); setResults([]) }}>
              {group.name || 'Unnamed group'}
            </MenuItem>
          ))}
        </Paper>}
      {!searching && name.length >= 2 && results.length === 0 &&
        <Typography variant='body2' className='text-gray-500 mt-1'>No groups found.</Typography>}
    </div>
  )
}

GroupSearch.propTypes = {
  groupId: PropTypes.string.isRequired,
  selected: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired
}

export default GroupSearch
